const { registerBlockType } = wp.blocks;
const { RichText, MediaUpload, MediaUploadCheck, InspectorControls } =
  wp.blockEditor;
const { Button, PanelBody, SelectControl } = wp.components;
const { Fragment } = wp.element;
const { __ } = wp.i18n;

const BlockEdit = ({ attributes, setAttributes }) => {
  const { sectionClass, cornerPositionClass, cornerBackgroundClass, widgets } =
    attributes;

  const addWidget = (type) => {
    const newWidgets = [
      ...widgets,
      {
        type,
        title: '',
        content: '',
        imgUrl: '',
        imgAlt: '', // Added imgAlt for alt text
        description: '',
      },
    ];
    setAttributes({ widgets: newWidgets });
  };

  const removeWidget = (index) => {
    const newWidgets = widgets.filter((_, i) => i !== index);
    setAttributes({ widgets: newWidgets });
  };

  const updateWidget = (index, updates) => {
    const newWidgets = widgets.map((widget, i) =>
      i === index ? { ...widget, ...updates } : widget
    );
    setAttributes({ widgets: newWidgets });
  };

  return (
    <Fragment>
      <InspectorControls>
        <PanelBody title={__('Section Settings')}>
          <SelectControl
            label={__('Section Class')}
            value={sectionClass}
            options={[
              { label: 'Dark Secondary', value: 'section-dark-secondary' },
              { label: 'Dark Primary', value: 'section-dark-primary' },
              { label: 'Light Primary', value: 'section-light-primary' },
              { label: 'Light Secondary', value: 'section-light-secondary' },
            ]}
            onChange={(value) => setAttributes({ sectionClass: value })}
          />
          <SelectControl
            label={__('Corner Position')}
            value={cornerPositionClass}
            options={[
              { label: 'None', value: 'corner-disabled' },
              { label: 'Left-Top', value: 'corner-left-top' },
              { label: 'Left-Bottom', value: 'corner-left-bottom' },
              { label: 'Right-Top', value: 'corner-right-top' },
              { label: 'Right-Bottom', value: 'corner-right-bottom' },
            ]}
            onChange={(value) => setAttributes({ cornerPositionClass: value })}
          />
          <SelectControl
            label={__('Corner Background')}
            value={cornerBackgroundClass}
            options={[
              { label: 'Dark Primary', value: 'corner-on-dark-primary' },
              { label: 'Dark Secondary', value: 'corner-on-dark-secondary' },
              { label: 'Light Primary', value: 'corner-on-light-primary' },
              { label: 'Light Secondary', value: 'corner-on-light-secondary' },
            ]}
            onChange={(value) =>
              setAttributes({ cornerBackgroundClass: value })
            }
          />
        </PanelBody>
        <PanelBody title={__('Add Widget')}>
          <Button isPrimary onClick={() => addWidget('widget-text')}>
            + {__('Add Text Widget')}
          </Button>
          <Button isPrimary onClick={() => addWidget('widget-simple')}>
            + {__('Add Simple Widget')}
          </Button>
          <Button isPrimary onClick={() => addWidget('widget-photo')}>
            + {__('Add Photo Widget')}
          </Button>
        </PanelBody>
      </InspectorControls>
      <section className={sectionClass}>
        <div
          className={`corner ${cornerPositionClass} ${sectionClass.replace(
            'section',
            'corner'
          )} ${cornerBackgroundClass}`}
        ></div>
        <div className="widgets-grid story-grid">
          {widgets.map((widget, index) => (
            <div className={`widget ${widget.type}`} key={index}>
              <div className="content">
                {widget.type === 'widget-text' && (
                  <>
                    <RichText
                      tagName="h2"
                      className="header-text"
                      value={widget.title}
                      onChange={(title) => updateWidget(index, { title })}
                      placeholder={__('Title')}
                    />
                  </>
                )}
                {widget.type === 'widget-simple' && (
                  <>
                    <RichText
                      tagName="strong"
                      className={'header-text'}
                      value={widget.title}
                      onChange={(title) => updateWidget(index, { title })}
                      placeholder={__('Title')}
                    />
                    <RichText
                      tagName="div"
                      className="description"
                      value={widget.description}
                      onChange={(description) =>
                        updateWidget(index, { description })
                      }
                      placeholder={__('Description')}
                    />
                  </>
                )}
                {widget.type === 'widget-photo' && (
                  <>
                    <div>
                      <MediaUploadCheck>
                        <MediaUpload
                          onSelect={(media) =>
                            updateWidget(index, {
                              imgUrl: media.url,
                              imgAlt: media.alt || '', // Set imgAlt
                            })
                          }
                          allowedTypes={['image']}
                          value={widget.imgUrl}
                          render={({ open }) => (
                            <Button onClick={open} isPrimary isSmall>
                              {widget.imgUrl
                                ? __('Change Image')
                                : __('Upload Image')}
                            </Button>
                          )}
                        />
                      </MediaUploadCheck>
                      {widget.imgUrl && (
                        <img src={widget.imgUrl} alt={widget.imgAlt || ''} />
                      )}
                    </div>
                    <RichText
                      tagName="strong"
                      value={widget.title}
                      className={'header-text'}
                      onChange={(title) => updateWidget(index, { title })}
                      placeholder={__('Title')}
                    />
                    <RichText
                      tagName="div"
                      className="description"
                      value={widget.description}
                      onChange={(description) =>
                        updateWidget(index, { description })
                      }
                      placeholder={__('Description')}
                    />
                  </>
                )}
                <Button
                  isDestructive
                  onClick={() => removeWidget(index)}
                  style={{ marginTop: '10px' }}
                >
                  {__('Remove Widget')}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </Fragment>
  );
};

registerBlockType('pg/widgets-section', {
  title: __('PG Widgets Section'),
  icon: 'screenoptions',
  category: 'layout',
  supports: {
    html: false,
  },
  attributes: {
    sectionClass: {
      type: 'string',
      default: 'section-light-secondary',
    },
    cornerPositionClass: {
      type: 'string',
      default: 'corner-disabled',
    },
    cornerBackgroundClass: {
      type: 'string',
      default: 'corner-on-light-secondary',
    },
    widgets: {
      type: 'array',
      default: [],
    },
  },
  edit: BlockEdit,
  save: ({ attributes }) => {
    const {
      sectionClass,
      cornerPositionClass,
      cornerBackgroundClass,
      widgets,
    } = attributes;

    return (
      <section className={sectionClass}>
        <div
          className={`corner ${cornerPositionClass} ${sectionClass.replace(
            'section',
            'corner'
          )} ${cornerBackgroundClass}`}
        ></div>
        <div className="widgets-grid story-grid">
          {widgets.map((widget, index) => (
            <div className={`widget ${widget.type}`} key={index}>
              <div className="content">
                {widget.type === 'widget-text' && (
                  <RichText.Content
                    tagName="h2"
                    className="header-text"
                    value={widget.title}
                  />
                )}
                {widget.type === 'widget-simple' && (
                  <>
                    <RichText.Content
                      className="header-text"
                      tagName="strong"
                      value={widget.title}
                    />
                    <div className="description">
                      <RichText.Content
                        tagName="p"
                        value={widget.description}
                      />
                    </div>
                  </>
                )}
                {widget.type === 'widget-photo' && (
                  <>
                    <div>
                      {widget.imgUrl && (
                        <img src={widget.imgUrl} alt={widget.imgAlt || ''} />
                      )}
                    </div>
                    <RichText.Content
                      tagName="strong"
                      className="header-text"
                      value={widget.title}
                    />
                    <div className="description">
                      <RichText.Content
                        tagName="p"
                        value={widget.description}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  },
});
